import { useState, useEffect, useCallback } from 'react';

import styles from './stylesLiveEvent.module.css';

import CustomAutoCompleteControlled from '../../../CommonComponents/CustomAutoComplete/CustomAutoCompleteControlled';
import TableTabs from './TableTabs/TableTabs';
import SortingTable from './SortingTable/SortingTable';
import { useGetSportsOrdering } from '../../../../queries/useGetSportsOrdering';
import { useGetLeaguesBySportId } from '../../../../queries/useGetLeaguesBySportId';

function LiveEvent({ activeTab, setActiveTab }) {
  const [queryValue, setQueryValue] = useState();
  const [selectedSportId, setSelectedSportId] = useState();

  const { data: liveSports } = useGetSportsOrdering();
  const { data: leaguesData } = useGetLeaguesBySportId(selectedSportId);

  const [leagues, setLeagues] = useState([]);

  const buildSportsTabsContent = (leagues) => {
    if (!leagues) return [];

    return leagues.map((league) => {
      let pinCounter = 1;

      const pinnedEvents = [];
      const otherEvents = [];

      league.events.forEach((event) => {
        if (event.is_pinned) {
          pinnedEvents.push(event);
        } else {
          otherEvents.push(event);
        }
      });

      const processedPinnedEvents = pinnedEvents.map((event) => {
        const pinned_order = event.pinned_order || String(pinCounter++);
        return {
          ...event,
          event_id: event.event_id,
          name: event.name,
          pinned_order,
          display_order: event.display_order,
        };
      });

      const processedOtherEvents = otherEvents.map((event) => ({
        ...event,
        event_id: event.event_id,
        name: event.name,
        pinned_order: event.pinned_order,
        display_order: event.display_order,
      }));

      const sortedEvents = [...processedPinnedEvents, ...processedOtherEvents];

      return {
        ...league,
        id: league.league_id,
        etype: 'category',
        ename: league.league_name,
        events: sortedEvents,
      };
    });
  };

  const sortEvents = (event) => {
    return event.slice().sort((a, b) => {
      if (a.pinned_order && !b.pinned_order) return -1;
      if (!a.pinned_order && b.pinned_order) return 1;

      if (a.pinned_order && b.pinned_order) {
        return Number(a.pinned_order) - Number(b.pinned_order);
      }

      if (a.display_order && b.display_order) {
        return Number(a.display_order) - Number(b.display_order);
      }
      if (a.display_order && !b.display_order) return -1;
      if (!a.display_order && b.display_order) return 1;

      return 0;
    });
  };

  const buildFilteredCategories = useCallback((leaguesArr, query) => {
    const liveEventsBySport = buildSportsTabsContent(leaguesArr);

    if (!liveEventsBySport || liveEventsBySport.length === 0) return [];

    let leagues = liveEventsBySport;

    if (query) {
      leagues = leagues
        .map((league) => {
          const leagueMatch = league?.ename
            ?.toLowerCase()
            .includes(query.toLowerCase());
          const eventMatches = league.events.filter((event) =>
            event?.competitors
              .flatMap((competitor) => competitor.name)
              .join(' vs ')
              ?.toLowerCase()
              .includes(query.toLowerCase())
          );

          const finalEvents = leagueMatch ? league.events : eventMatches;
          return { ...league, events: sortEvents(finalEvents), expand: true };
        })
        .filter((league) => league.events.length > 0);
    } else {
      leagues = leagues.map((league) => ({
        ...league,
        events: sortEvents(league.events),
      }));
    }

    return leagues;
  }, []);

  useEffect(() => {
    if (liveSports?.length > 0 && !selectedSportId) {
      setSelectedSportId(liveSports[0].sport_id);
    }
  }, [liveSports, selectedSportId]);

  useEffect(() => {
    if (leaguesData) {
      setLeagues(buildFilteredCategories(leaguesData, queryValue));
    }
  }, [leaguesData, queryValue, buildFilteredCategories]);

  const handleSearch = (query) => {
    setQueryValue(query);
  };

  const togglePin = async ({ name, event_id }) => {
    setLeagues((prevData) =>
      prevData.map((league) => {
        if (league.ename !== name) return league;

        const targetEvent = league.events.find(
          (event) => event.event_id === event_id
        );
        if (!targetEvent) return league;

        const isPinned = !!targetEvent.pinned_order;

        let newEvents;
        if (!isPinned) {
          newEvents = league.events
            .map((event) => ({
              ...event,
              pinned_order: event.pinned_order
                ? String(Number(event.pinned_order) + 1)
                : undefined,
            }))
            .map((event) =>
              event.event_id === event_id
                ? { ...event, pinned_order: '1' }
                : event
            );
        } else {
          const removedOrder = Number(targetEvent.pinned_order);
          newEvents = league.events.map((event) => {
            if (!event.pinned_order) return event;
            const order = Number(event.pinned_order);
            if (event.event_id === event_id)
              return { ...event, pinned_order: undefined };

            if (order > removedOrder)
              return { ...event, pinned_order: String(order - 1) };

            return event;
          });
        }

        return { ...league, events: sortEvents(newEvents) };
      })
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.liveEvent}>
        <>
          <div className={styles.searchInputContainer}>
            <div className={styles.searchInput}>
              <CustomAutoCompleteControlled
                placeholder="Search"
                value={queryValue}
                handleChange={(value) => handleSearch(value)}
                allOptions={[]}
                clearOptions={() => handleSearch(null)}
                selector="value"
              />
            </div>
          </div>

          <TableTabs
            liveSports={liveSports}
            onSelectSport={(id) => setSelectedSportId(id)}
          />

          <div className={styles.tableContainer}>
            <SortingTable
              columns={[
                'League',
                'ID',
                'Event Name',
                'Event Date',
                'Status',
                'Country',
                'Scores',
                'Provider',
                'Number of bets',
                'Live activity',
              ]}
              leagues={leagues}
              setLeagues={setLeagues}
              toggleStatus={() => {}}
              togglePin={togglePin}
              etype="category"
              sportId={selectedSportId}
            />
          </div>
        </>
      </div>
    </div>
  );
}

export default LiveEvent;
